import ExpandableFilterPanel, {
  ExpandableFilterPanelItem,
} from 'components/molecules/expandable-filter-panel';
import { useTranslation } from 'react-i18next';
import BooleanSelectBox from 'components/molecules/boolean-select-box';
import ClinicSelectBox from 'components/molecules/clinic-select-box';
import DoctorSelectBox from 'components/molecules/doctor-select-box';
import PatientSelectBox from 'components/molecules/patient-select-box';
import { useOnboardingStatusQueryFilter } from 'infrastructure/hooks/rpm/onboarding-status-report';
import RpmStatusSelectBox from 'components/molecules/rpm-status-select-box';

import s from './styles.module.scss';

import type { IRpmOnboardingStatusReportFilters } from 'infrastructure/interfaces';
import type { FC } from 'react';

interface CcmOnboardingFilterProps {}

const RpmOnboardingFilter: FC<CcmOnboardingFilterProps> = () => {
  const { count, filter, resetFilter, updateFilter } =
    useOnboardingStatusQueryFilter();
  const { t } = useTranslation();

  const onClinicChange = (clinicGuids: string[]) => {
    const newFilter: Partial<IRpmOnboardingStatusReportFilters> = {
      clinicGuid: clinicGuids,
      doctorGuid: undefined,
      patientGuid: undefined,
      page: undefined,
    };

    if (!clinicGuids.length) {
      newFilter.childClinic = undefined;
    }
    updateFilter(newFilter);
  };

  const onChildClinicChange = (value: any) => {
    const childClinic = typeof value === 'boolean' ? String(value) : value;
    updateFilter({ childClinic, page: undefined });
  };

  const onDoctorChange = (doctorGuid: string[]) => {
    updateFilter({
      doctorGuid: doctorGuid.length ? doctorGuid : undefined,
      patientGuid: undefined,
      page: undefined,
    });
  };

  const onReset = () => {
    resetFilter();
  };

  return (
    <ExpandableFilterPanel filtersCount={count} onReset={onReset} isExpanded>
      <div className={s.wrapper}>
        <ExpandableFilterPanelItem
          borderBottom
          label={t('labels.patientFilter')}
        >
          <PatientSelectBox
            clinicGuid={filter.clinicGuid}
            doctorGuid={filter.doctorGuid}
            placeholder={t('labels.searchPatient')}
            value={filter.patientGuid}
            onChange={(patientGuid) => {
              updateFilter({ patientGuid, page: undefined });
            }}
            allowClear
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.status')} borderBottom>
          <RpmStatusSelectBox
            value={filter.status}
            onChange={(v) => {
              updateFilter({ status: v, page: undefined });
            }}
            allowClear
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.clinic')} borderBottom>
          <ClinicSelectBox
            allowClear
            placeholder={t('labels.search')}
            value={filter.clinicGuid}
            onChange={(clinicGuid, options) => {
              if (Array.isArray(clinicGuid) && Array.isArray(options)) {
                onClinicChange(clinicGuid);
              } else {
                onClinicChange([clinicGuid]);
              }
            }}
            mode="multiple"
            parentClinicsOnly={false}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.childClinics')}>
          <BooleanSelectBox
            value={filter.childClinic}
            trueLabel={t('labels.included')}
            falseLabel={t('labels.excluded')}
            onChange={onChildClinicChange}
            disabled={!filter.clinicGuid?.length}
            allowClear
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.provider')}>
          <DoctorSelectBox
            allowClear
            clinicGuid={filter.clinicGuid}
            placeholder={t('labels.search')}
            mode="multiple"
            value={filter.doctorGuid}
            onChange={(doctorGuid, options) => {
              if (Array.isArray(doctorGuid) && Array.isArray(options)) {
                onDoctorChange(doctorGuid);
              } else {
                onDoctorChange([doctorGuid]);
              }
            }}
          />
        </ExpandableFilterPanelItem>
      </div>
    </ExpandableFilterPanel>
  );
};
export default RpmOnboardingFilter;
